import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import i18n, { RTL_LANGUAGES } from "@/i18n/config";

const LANG_PREF_KEY = "td_lang_pref";

export function useLanguage() {
  const { user } = useAuth();
  const [language, setLanguageState] = useState(i18n.language || "en");

  const applyLanguage = (lang: string) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang);
    document.documentElement.dir = RTL_LANGUAGES.has(lang) ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  };

  // Sync from i18n when language changes elsewhere (e.g. Settings page)
  useEffect(() => {
    const handler = (lng: string) => setLanguageState(lng);
    i18n.on("languageChanged", handler);
    return () => { i18n.off("languageChanged", handler); };
  }, []);

  // Load preferred language from DB on login.
  // We respect a deliberate local choice (made via the Settings or onboarding
  // language switcher) over the DB value — the local choice may be newer than
  // what was last saved to `profiles.preferred_language`.
  useEffect(() => {
    if (!user) return;
    let localChoice: string | null = null;
    try { localChoice = localStorage.getItem(LANG_PREF_KEY); } catch {}
    supabase
      .from("profiles")
      .select("preferred_language")
      .eq("user_id", user.id)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        const dbLang = data?.preferred_language;
        if (!dbLang) return;
        // If user has an explicit local pref that differs from DB, the local
        // pref wins (it'll be persisted to DB on next save). Otherwise apply
        // DB value so language follows them across devices.
        if (localChoice && localChoice !== dbLang) return;
        if (dbLang !== i18n.language) applyLanguage(dbLang);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return language;
}
